import * as Sentry from '@sentry/react';
import { contracts, createTypedClient, type Fetcher, type FetcherInit, type FetcherResponse } from '@immo/api-contract';
import { env } from '@/lib/env';
import { parseApiError } from '@/lib/errors/parse-api-error';
import { notifyReleaseMismatch } from '@/lib/release-notice';
import { getAccessToken, setAccessToken } from './access-token-store';

/**
 * Typed cross-app API clients. Walks the `contracts` tree to expose each
 * endpoint as a typed method (e.g. `apiClient.auth.me()`). The custom
 * fetcher adds three things on top of a plain `fetch`:
 *
 *   1. Attaches `Authorization: Bearer <access token>` when the in-memory
 *      store has one. Skipped explicitly for the client's refresh route —
 *      that route authenticates via the refresh cookie, not the bearer.
 *   2. Always sends `X-Immo-Client: web` and `credentials: 'include'`. The
 *      former is the third CSRF belt on the refresh routes; the latter is
 *      required for the cookie round-trip on the refresh path.
 *   3. On a 401, refreshes the access token once, replays the original request
 *      with the new token, and gives up after a single retry — failing only if
 *      the refresh itself fails. `redirectToSignIn` is the caller's responsibility.
 *
 * Two clients share this machinery because member and partner sessions refresh
 * differently. `apiClient` (members) refreshes via `/v1/auth/refresh`;
 * `onboardingApiClient` (partners mid-onboarding) refreshes via the partner
 * route, because the member endpoint rejects partners — they hold no workspace
 * membership until approval (IMM-308). Both rotate the same in-memory token
 * store; a tab is only ever one or the other.
 */

const API_ORIGIN = env.apiOrigin;

const MEMBER_REFRESH_PATH = contracts.auth.refresh.path;
const ONBOARDING_REFRESH_PATH = contracts.organizationOnboarding.refreshSession.path;

type RefreshOnce = () => Promise<string | null>;

function createClient(refreshPath: string, refresh: RefreshOnce) {
  const fetcher: Fetcher = async (url, init) => {
    const send = async (): Promise<FetcherResponse> => {
      const initial = await sendRequest(url, decorate(url, init, getAccessToken(), refreshPath));

      if (initial.status !== 401 || url.endsWith(refreshPath)) {
        return wrapResponse(initial);
      }

      const refreshed = await refresh();

      if (refreshed === null) {
        return wrapResponse(initial);
      }

      return wrapResponse(await sendRequest(url, decorate(url, init, refreshed, refreshPath)));
    };

    const response = await send();

    // A 403 means the UI let the user attempt an action their permissions forbid
    // — i.e. a component rendered (a button, a panel, a route) that should have
    // been gated. Report every one so we can find and gate the leak; the request
    // still returns normally to its caller (this is telemetry, not flow control).
    if (response.status === 403) reportForbidden(url, init);

    return response;
  };

  return createTypedClient(contracts, {
    baseUrl: API_ORIGIN,
    fetcher,
    // Both spread rather than assigned: `exactOptionalPropertyTypes` rejects an
    // explicit `undefined`, and an absent release is the normal state in dev and
    // preview, where the comparison must simply never run.
    ...(env.sentry.release !== undefined
      ? { release: env.sentry.release, onReleaseMismatch: notifyReleaseMismatch }
      : {}),
  });
}

/**
 * `fetch`, with its transport failures typed. A rejected `fetch` — API
 * unreachable, DNS failure, connection reset mid-outage, offline, aborted —
 * produces no HTTP response at all, so it must go through the shared parser and
 * come out as a `NetworkError` (ADR 0014). Left unconverted it escapes as a bare
 * `TypeError: Failed to fetch` and every downstream boundary treats it as an
 * unknown frontend crash instead of the retryable outage it is.
 */
async function sendRequest(url: string, init: RequestInit): Promise<Response> {
  try {
    return await fetch(url, init);
  } catch (cause) {
    throw await parseApiError(cause);
  }
}

const ID_SEGMENT = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi;

/**
 * Captures a forbidden (403) API response to Sentry as a signal that the front
 * end surfaced something the caller lacks permission for. The route is
 * normalised (ids → `:id`, query string dropped) so issues group by endpoint,
 * and only the method + route ride along — never the body or any PII. A no-op
 * when Sentry is not initialised (`captureException` swallows it).
 */
function reportForbidden(url: string, init: FetcherInit): void {
  let route = url;

  try {
    route = new URL(url).pathname.replace(ID_SEGMENT, ':id');
  } catch {
    // Non-absolute URL — fall back to the raw value.
  }

  const method = (init.method ?? 'GET').toUpperCase();

  Sentry.withScope((scope) => {
    // `warning`, not `error`: this fires for every 403 app-wide, and some are
    // expected denials (onboarding probes, race-y authz) rather than UI leaks.
    // Warning keeps the leak signal without drowning the error channel in noise.
    scope.setLevel('warning');
    scope.setTag('forbidden.method', method);
    scope.setTag('forbidden.route', route);
    // Group by (method, route) so each leaking endpoint is one issue.
    scope.setFingerprint(['forbidden-ui-leak', method, route]);
    Sentry.captureException(new Error(`Forbidden UI action: ${method} ${route}`));
  });
}

function decorate(url: string, init: FetcherInit, accessToken: string | null, refreshPath: string): RequestInit {
  const headers: Record<string, string> = {
    ...init.headers,
    'X-Immo-Client': 'web',
  };

  if (accessToken !== null && !url.endsWith(refreshPath)) {
    headers['Authorization'] = `Bearer ${accessToken}`;
  }

  return {
    method: init.method,
    headers,
    ...(init.body !== undefined ? { body: init.body } : {}),
    credentials: 'include',
  };
}

function wrapResponse(response: Response): FetcherResponse {
  return {
    status: response.status,
    text: () => response.text(),
    header: (name) => response.headers.get(name),
  };
}

/**
 * Wraps a refresh call so concurrent 401s trigger a single in-flight refresh
 * (the rest await its result) and the in-memory token store is kept in sync —
 * cleared on any failure so a stale token never lingers.
 */
function createRefreshOnce(perform: () => Promise<string | null>): RefreshOnce {
  let inflight: Promise<string | null> | null = null;

  return () => {
    if (inflight !== null) {
      return inflight;
    }

    inflight = (async () => {
      try {
        const accessToken = await perform();

        setAccessToken(accessToken);

        return accessToken;
      } catch {
        setAccessToken(null);

        return null;
      } finally {
        inflight = null;
      }
    })();

    return inflight;
  };
}

const refreshMemberAccessToken = createRefreshOnce(async () => {
  const result = await apiClient.auth.refresh({ body: {} });

  return result.ok ? result.body.accessToken : null;
});

const refreshOnboardingAccessToken = createRefreshOnce(async () => {
  const result = await onboardingApiClient.organizationOnboarding.refreshSession({ body: {} });

  return result.ok ? result.body.accessToken : null;
});

export const apiClient = createClient(MEMBER_REFRESH_PATH, refreshMemberAccessToken);

/**
 * Forces a single member access-token refresh and returns the new token (or
 * null). Exposed for the assistant SSE reader, which fetches the run-progress
 * stream outside the typed client and must re-authenticate a 401 mid-run on its
 * own (the typed client's auto-refresh only wraps its own JSON calls).
 */
export const refreshAccessToken: RefreshOnce = refreshMemberAccessToken;

export const onboardingApiClient = createClient(ONBOARDING_REFRESH_PATH, refreshOnboardingAccessToken);

export type ApiClient = typeof apiClient;
